import { Injectable, Provider } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import dayjs from 'dayjs';

import { parseTypedDate } from '../utils/date.utils';

/** dayjs localized tokens (`L`, `LL`, …) as the active locale's pattern, so they can be read strictly. */
function expandLocalizedFormat(format: string): string {
    return /^(LTS?|L{1,4}|l{1,4})$/.test(format) ? dayjs().localeData().longDateFormat(format) : format;
}

/**
 * The one date adapter for every Material datepicker in the app (LIQ-2056).
 *
 * Values stay JavaScript `Date`s, as with Material's native adapter, so forms and API code are
 * unchanged. Display and typed input go through dayjs in the active locale (set before the app loads
 * by locale-bootstrap.ts), so a picker shows and reads dates the same way as the rest of the app: an
 * NZ user sees and types 04/03/2026 for 4 March, and the calendar starts on Monday.
 *
 * Material's native adapter shows dates with the browser's Intl formatter but reads typed text with
 * `Date.parse`, which always assumes US order, so it can't be used with a non-US locale.
 */
@Injectable()
export class LiqDateAdapter extends NativeDateAdapter {
    override format(date: Date, displayFormat: object | string): string {
        // Formats are dayjs strings (LIQ_DATE_FORMATS); fall back for any Intl-style object.
        if (typeof displayFormat !== 'string') {
            return super.format(date, displayFormat);
        }
        return dayjs(date).format(displayFormat);
    }

    override parse(value: unknown, parseFormat: unknown): Date | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value !== 'string') {
            return super.parse(value, parseFormat);
        }
        const formats = (Array.isArray(parseFormat) ? parseFormat : [parseFormat])
            .filter((f): f is string => typeof f === 'string')
            .map(expandLocalizedFormat);
        if (!formats.length) {
            return super.parse(value, parseFormat);
        }
        for (const format of formats) {
            const parsed = parseTypedDate(value, format);
            if (parsed.isValid()) {
                return parsed.toDate();
            }
        }
        // Text that isn't a date in the locale's order: an invalid Date, so the input shows its
        // "invalid date" state instead of silently taking some other date.
        return this.invalid();
    }

    // Month and weekday names come from the native adapter: the browser's names for the locale
    // (MAT_DATE_LOCALE follows LOCALE_ID), e.g. the calendar header's D L M X J V S in Spanish. Only the
    // week start is taken from dayjs, so the calendar starts on the same day as the rest of the app.
    override getFirstDayOfWeek(): number {
        return dayjs.localeData().firstDayOfWeek();
    }
}

/** Display and parse formats for the app's datepickers: the active locale's numeric short date. */
export const LIQ_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'L'
    },
    display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY'
    }
};

/** Root providers: the dayjs adapter and its formats. Don't also import MatNativeDateModule anywhere. */
export function provideLiqDateAdapter(): Provider[] {
    return [
        { provide: DateAdapter, useClass: LiqDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: LIQ_DATE_FORMATS }
    ];
}
